import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { createAPIClient, IPropertiesApi } from '../api';
import { getErrorMessage } from '../utils/apiError';

/**
 * /properties/:id: looks the property up and redirects to its page under
 * its customer (/customers/:customerId/properties/:id). Used where only the
 * property id is known, e.g. audit log rows whose target is a property
 * (tracker item 47).
 */
export function PropertyRedirectPage() {
  const { id = '' } = useParams<{ id: string }>();
  const api = useMemo<IPropertiesApi>(() => createAPIClient(), []);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setCustomerId(null);
    setError(null);
    api.getProperty(id)
      .then((property) => { if (active) setCustomerId(property.customerId); })
      .catch((err) => { if (active) setError(getErrorMessage(err, 'Property not found')); });
    return () => { active = false; };
  }, [api, id]);

  if (customerId) {
    return <Navigate replace to={`/customers/${encodeURIComponent(customerId)}/properties/${encodeURIComponent(id)}`} />;
  }
  return (
    <main className="max-w-3xl mx-auto px-4 py-8">
      {error ? (
        <div role="alert" className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <p className="text-gray-800">This property could not be opened: {error}</p>
          <Link to="/customers" className="mt-3 inline-block text-blue-700 hover:underline">Back to customers</Link>
        </div>
      ) : (
        <p role="status" className="text-gray-500">Opening property...</p>
      )}
    </main>
  );
}
